// What a viewer may do to ONE record shown inside a process screen.
//
// The process overview and the trail both draw records that belong to some
// object's list, and both have to offer exactly what that list offers — the
// custom action buttons the engine did not hide for this row, Edit or View by
// the row's own verdict, the trail, the audit trail, a way out to the object's
// own screen, and Delete last. Two copies of that would be two orders, two sets
// of gates and one of them quietly wrong, so it is built here once.
//
// Only the host's OWN object gets the acting half. Running an action is
// pre-validations, a confirmation gate, acknowledgeable warnings and a result
// that can rewrite the form — all of it implemented, rights-gated, on the
// transaction screen for the object it is showing. A record of ANOTHER object
// is offered what can honestly be offered from here: its trail, its history,
// and the way to its own screen, where its own actions live.

import {
  ExternalLink, Eye, History, Pencil, Play, Tags, Trash2, UserPlus, Waypoints,
} from 'lucide-react'

import type { RowAction } from '../list/RowActionsMenu'
import { openRecord, visibleRowActions } from '../utils'
import type { NodeActions } from './contract'

export interface RecordActionsOptions {
  txnId: string
  /** The engine's per-row verdicts, as they arrived. */
  editable?: boolean
  hiddenActions?: string[]
  /** The host's handlers — pass null when this record belongs to a different
   *  object from the one the host is showing. */
  own?: NodeActions | null
  /** Run one of the object's action buttons (the caller refreshes after). */
  onRun?: (name: string) => void
  /** Delete it (the caller refreshes after). */
  onDelete?: () => void
  /** Open this record's process trail. Omitted inside the trail itself. */
  onProcessTrail?: () => void
  /** Take the viewer to the record on its own screen. */
  onOpenFullScreen?: () => void
}

export function buildRecordActions(o: RecordActionsOptions): RowAction[] {
  const out: RowAction[] = []
  const own = o.own || null
  const editable = o.editable !== false
  if (own) {
    for (const a of visibleRowActions(own.rowActions, o.hiddenActions)) {
      out.push({
        label: a.name,
        icon: <Play size={14} />,
        kind: 'run',
        onClick: () => o.onRun?.(a.name),
      })
    }
    if (own.onEdit && own.canEdit !== false) {
      // Opened through the object's own edit / view system action, so the
      // conditions the designer put on editing ("only an Open deal") gate this
      // exactly as they gate the pencil on the list. Calling the editor
      // directly here would let the trail walk past a rule the list enforces.
      out.push({
        label: editable ? (own.systemOverrides?.edit?.name || 'Edit') : 'View',
        icon: editable ? <Pencil size={14} /> : <Eye size={14} />,
        kind: 'open',
        onClick: () => openRecord({
          txnId: o.txnId,
          editable,
          systemOverrides: own.systemOverrides,
          onRunRowAction: own.onRunRowAction,
          onOpen: (id) => own.onEdit!(id),
        }),
      })
    }
    if (own.onAssign) {
      out.push({
        label: 'Assign to\u2026',
        icon: <UserPlus size={14} />,
        kind: 'assign',
        onClick: () => own.onAssign!(o.txnId),
      })
    }
    if (own.onAlias) {
      out.push({
        label: 'Also known as',
        icon: <Tags size={14} />,
        kind: 'alias',
        onClick: () => own.onAlias!(o.txnId),
      })
    }
  }
  if (o.onProcessTrail) {
    out.push({
      label: 'Process trail',
      icon: <Waypoints size={14} />,
      kind: 'trail',
      onClick: o.onProcessTrail,
    })
  }
  if (own?.onHistory) {
    out.push({
      label: 'Audit trail',
      icon: <History size={14} />,
      kind: 'history',
      onClick: () => own.onHistory!(o.txnId),
    })
  }
  if (o.onOpenFullScreen) {
    out.push({
      label: 'Open full screen',
      icon: <ExternalLink size={14} />,
      kind: 'full',
      onClick: o.onOpenFullScreen,
    })
  }
  // A merged system Delete's show-when hides the built-in Delete too.
  const delOverride = own?.systemOverrides?.delete
  if (own?.onDelete && own.canDelete !== false && editable && o.onDelete
      && (!delOverride || visibleRowActions([delOverride], o.hiddenActions).length)) {
    out.push({
      // A system Delete action the designer defined names this control.
      label: own.systemOverrides?.delete?.name || 'Delete',
      icon: <Trash2 size={14} />,
      danger: true,
      kind: 'delete',
      onClick: o.onDelete,
    })
  }
  return out
}

/** Split the menu into the one or two things a surface with ROOM should draw as
 *  buttons, and everything that stays in the kebab.
 *
 *  A pane wide enough to show the record's form is wide enough to show the one
 *  thing people opened it to do — hiding Edit behind a three-dot menu on a
 *  surface that had space for it is the small cruelty this exists to avoid. */
export function splitRecordActions(
  actions: RowAction[],
): { primary: RowAction[]; menu: RowAction[] } {
  const primary = actions.filter((a) => a.kind === 'open' || a.kind === 'full')
  return { primary, menu: actions.filter((a) => !primary.includes(a)) }
}
